import { useState, useEffect, useRef, memo } from 'react'
import './styles.css'
import Nav from './Nav'
import Anim from './Anim'
const TimeDisplay = memo(function TimeDisplay() {
  const [time, setTime] = useState(new Date())
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])
  const hours = time.getHours().toString().padStart(2, '0')
  const minutes = time.getMinutes().toString().padStart(2, '0')
  const seconds = time.getSeconds().toString().padStart(2, '0')
  return (
    <p className="header-info-2">
      <span className="time-label">Local time</span>
      <span className="time-value">{hours}:{minutes}:{seconds}</span>
    </p>
  )
})
export default function Overlay() {
  const locationTagRef = useRef(null)
  const workTitleRef = useRef(null)
  const scrollIndicatorRef = useRef(null)
  useEffect(() => {
    let ticking = false
    let lastScrollY = 0
    const handleScroll = () => {
      lastScrollY = window.scrollY
      if (!ticking) {
        requestAnimationFrame(() => {
          if (scrollIndicatorRef.current) {
            const scrollPercent = (lastScrollY / window.innerHeight) * 100
            if (scrollPercent > 50) {
              scrollIndicatorRef.current.classList.add('hidden')
            } else {
              scrollIndicatorRef.current.classList.remove('hidden')
            }
          }
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  useEffect(() => {
    const sections = document.querySelectorAll('.section-2, .section-3')
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3
    }
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active')
        }
      })
    }
    const observer = new IntersectionObserver(observerCallback, observerOptions)
    sections.forEach(section => {
      observer.observe(section)
    })
    return () => {
      sections.forEach(section => {
        observer.unobserve(section)
      })
    }
  }, [])
  return (
    <div className="container">
      { }
      <Nav />
      { }
      <Anim locationTagRef={locationTagRef} workTitleRef={workTitleRef} />
      <div className="main-wrapper">
        { }
        <section id="home" className="section section-1">
          <div className='header-h1-wrapper'>
            <h1 className="header-h1">Sadat Dev</h1>
          </div>
          <div className='header-info-wrapper'>
            <p className="header-info-1">Creating<br />Revenue</p>
            <TimeDisplay />
          </div>
          { }
          <div ref={scrollIndicatorRef} className="section-1-scroll-indicator">
            <span className="scroll-text">Scroll to explore</span>
            <div className="scroll-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </div>
          </div>
        </section>
        { }
        <section className="section section-2">
          <div className="about-content">
            <h2 ref={locationTagRef} className="location-tag">Based in <br /><strong>Canada</strong></h2>
          </div>
          <div className="work-content">
            <h2 ref={workTitleRef} className="work-title">Working <br /><strong>Worldwide</strong></h2>
          </div>
        </section>
        { }
        <section id="about" className="section section-3">
          <div className="section-3-content">
            <h2 className="section-3-title">About Me</h2>
            <div className="section-3-text">
              <p className="section-3-paragraph">
                I'm an AI Full-Stack Developer and Product Designer running Mirrin Design, shipping conversion-optimized web platforms for startups and e-commerce brands in a day. No templates. Just React, Next.js, and Figma executing at speed.
              </p>
              <p className="section-3-paragraph">
                At AfterQuery (YC W25) I'm engineering AI evaluation pipelines that doubled throughput and slashed model failure rates by 30%. I built dashboards that compressed analyst triage from hours to minutes. At Daylily AI, I built AI-driven shopping interfaces for 100 Thieves and Jazwares that drove 9% conversion lift and $50K+ revenue.
              </p>
              <p className="section-3-paragraph">
                At Velric I took a Next.js EdTech platform to 1500+ students on day one; shipped 25% ahead of schedule with 99.8% uptime. At Series I designed landing pages that lifted click-through rates by 35% and built a component library that cut review time by 40%.
              </p>
              <p className="section-3-paragraph">
                The scoreboard: $50K+ in client revenue, 5,500+ active users, 70% lift in engagement, 92% test coverage, and a component system that cut handoff time by 67%. I own the stack from Figma to production deploy.
              </p>
            </div>
          </div>
        </section>
        { }
        <section className="section section-4">
          <div className="section-4-content">
            <h2 className="section-4-title">Skills</h2>
            <div className="skills-grid">
              <div className="skill-item">
                <div className="skill-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="256" height="228" viewBox="0 0 256 228"><path fill="#00d8ff" d="M210.483 73.824a172 172 0 0 0-8.24-2.597c.465-1.9.893-3.777 1.273-5.621c6.238-30.281 2.16-54.676-11.769-62.708c-13.355-7.7-35.196.329-57.254 19.526a171 171 0 0 0-6.375 5.848a156 156 0 0 0-4.241-3.917C100.759 3.829 77.587-4.822 63.673 3.233C50.33 10.957 46.379 33.89 51.995 62.588a171 171 0 0 0 1.892 8.48c-3.28.932-6.445 1.924-9.474 2.98C17.309 83.498 0 98.307 0 113.668c0 15.865 18.582 31.778 46.812 41.427a146 146 0 0 0 6.921 2.165a168 168 0 0 0-2.01 9.138c-5.354 28.2-1.173 50.591 12.134 58.266c13.744 7.926 36.812-.22 59.273-19.855a146 146 0 0 0 5.342-4.923a168 168 0 0 0 6.92 6.314c21.758 18.722 43.246 26.282 56.54 18.586c13.731-7.949 18.194-32.003 12.4-61.268a145 145 0 0 0-1.535-6.842c1.62-.48 3.21-.974 4.76-1.488c29.348-9.723 48.443-25.443 48.443-41.52c0-15.417-17.868-30.326-45.517-39.844m-6.365 70.984q-2.102.694-4.3 1.345c-3.24-10.257-7.612-21.163-12.963-32.432c5.106-11 9.31-21.767 12.459-31.957c2.619.758 5.16 1.557 7.61 2.4c23.69 8.156 38.14 20.213 38.14 29.504c0 9.896-15.606 22.743-40.946 31.14m-10.514 20.834c2.562 12.94 2.927 24.64 1.23 33.787c-1.524 8.219-4.59 13.698-8.382 15.893c-8.067 4.67-25.32-1.4-43.927-17.412a157 157 0 0 1-6.437-5.87c7.214-7.889 14.423-17.06 21.459-27.246c12.376-1.098 24.068-2.894 34.671-5.345q.785 3.162 1.386 6.193M87.276 214.515c-7.882 2.783-14.16 2.863-17.955.675c-8.075-4.657-11.432-22.636-6.853-46.752a157 157 0 0 1 1.869-8.499c10.486 2.32 22.093 3.988 34.498 4.994c7.084 9.967 14.501 19.128 21.976 27.15a135 135 0 0 1-4.877 4.492c-9.933 8.682-19.886 14.842-28.658 17.94M50.35 144.747c-12.483-4.267-22.792-9.812-29.858-15.863c-6.35-5.437-9.555-10.836-9.555-15.216c0-9.322 13.897-21.212 37.076-29.293c2.813-.98 5.757-1.905 8.812-2.773c3.204 10.42 7.406 21.315 12.477 32.332c-5.137 11.18-9.399 22.249-12.634 32.792a135 135 0 0 1-6.318-1.979m12.378-84.26c-4.811-24.587-1.616-43.134 6.425-47.789c8.564-4.958 27.502 2.111 47.463 19.835a144 144 0 0 1 3.841 3.545c-7.438 7.987-14.787 17.08-21.808 26.988c-12.04 1.116-23.565 2.908-34.161 5.309a160 160 0 0 1-1.76-7.887m110.427 27.268a348 348 0 0 0-7.785-12.803c8.168 1.033 15.994 2.404 23.343 4.08c-2.206 7.072-4.956 14.465-8.193 22.045a381 381 0 0 0-7.365-13.322m-45.032-43.861c5.044 5.465 10.096 11.566 15.065 18.186a322 322 0 0 0-30.257-.006c4.974-6.559 10.069-12.652 15.192-18.18M82.802 87.83a323 323 0 0 0-7.227 13.238c-3.184-7.553-5.909-14.98-8.134-22.152c7.304-1.634 15.093-2.97 23.209-3.984a322 322 0 0 0-7.848 12.897m8.081 65.352c-8.385-.936-16.291-2.203-23.593-3.793c2.26-7.3 5.045-14.885 8.298-22.6a321 321 0 0 0 7.257 13.246c2.594 4.48 5.28 8.868 8.038 13.147m37.542 31.03c-5.184-5.592-10.354-11.779-15.403-18.433c4.902.192 9.899.29 14.978.29c5.218 0 10.376-.117 15.453-.343c-4.985 6.774-10.018 12.97-15.028 18.486m52.198-57.817c3.422 7.8 6.306 15.345 8.596 22.52c-7.422 1.694-15.436 3.058-23.88 4.071a382 382 0 0 0 7.859-13.026a347 347 0 0 0 7.425-13.565m-16.898 8.101a359 359 0 0 1-12.281 19.815a329 329 0 0 1-23.444.823c-7.967 0-15.716-.248-23.178-.732a310 310 0 0 1-12.513-19.846h.001a307 307 0 0 1-10.923-20.627a310 310 0 0 1 10.89-20.637l-.001.001a307 307 0 0 1 12.413-19.761c7.613-.576 15.42-.876 23.31-.876H128c7.926 0 15.743.303 23.354.883a329 329 0 0 1 12.335 19.695a359 359 0 0 1 11.036 20.54a330 330 0 0 1-11 20.722m22.56-122.124c8.572 4.944 11.906 24.881 6.52 51.026q-.518 2.504-1.15 5.09c-10.622-2.452-22.155-4.275-34.23-5.408c-7.034-10.017-14.323-19.124-21.64-27.008a161 161 0 0 1 5.888-5.4c18.9-16.447 36.564-22.941 44.612-18.3M128 90.808c12.625 0 22.86 10.235 22.86 22.86s-10.235 22.86-22.86 22.86s-22.86-10.235-22.86-22.86s10.235-22.86 22.86-22.86" /></svg>
                </div>
                <div className="skill-name">React</div>
              </div>
              <div className="skill-item">
                <div className="skill-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="#f5de19" d="M2 2h28v28H2z" /><path d="M20.809 23.875a2.87 2.87 0 0 0 2.6 1.6c1.09 0 1.787-.545 1.787-1.3c0-.9-.716-1.222-1.916-1.747l-.658-.282c-1.9-.809-3.16-1.822-3.16-3.964c0-1.973 1.5-3.476 3.853-3.476a3.89 3.89 0 0 1 3.742 2.107L25 18.128A1.79 1.79 0 0 0 23.311 17a1.145 1.145 0 0 0-1.259 1.128c0 .789.489 1.109 1.618 1.6l.658.282c2.236.959 3.5 1.936 3.5 4.133c0 2.369-1.861 3.667-4.36 3.667a5.06 5.06 0 0 1-4.795-2.691Zm-9.295.228c.413.733.789 1.353 1.693 1.353c.864 0 1.41-.338 1.41-1.653v-8.947h2.631v8.982c0 2.724-1.6 3.964-3.929 3.964a4.085 4.085 0 0 1-3.947-2.4Z" /></svg>
                </div>
                <div className="skill-name">JavaScript</div>
              </div>
              <div className="skill-item">
                <div className="skill-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256"><g fill="none"><rect width="256" height="256" fill="#fff" rx="60" /><path stroke="#000" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="4" d="M93.213 222.94L50 48l173.23 49.874z" clipRule="evenodd" /><path stroke="#000" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="4" d="m136.583 72.942l21.591 87.496l-86.567-24.945z" clipRule="evenodd" /><path stroke="#000" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="4" d="m115.103 147.36l-10.724-43.465l43.008 12.346zM93.65 60.483l10.725 43.465l-43.008-12.346zm86.013 24.777l10.724 43.465l-43.008-12.346zm-64.555 62.13l10.724 43.465l-43.008-12.346z" clipRule="evenodd" /></g></svg>
                </div>
                <div className="skill-name">Next.js & TS</div>
              </div>
              <div className="skill-item">
                <div className="skill-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256"><path fill="#F24E1E" d="M128 0H42.667C19.093 0 0 19.093 0 42.667v85.333C0 151.573 19.093 170.667 42.667 170.667S85.333 151.573 85.333 128V42.667h42.667c23.573 0 42.666 19.093 42.666 42.666S151.573 128 128 128z" /><path fill="#FF7262" d="M128 0h85.333C236.907 0 256 19.093 256 42.667S236.907 85.333 213.333 85.333H128z" /><path fill="#1ABCFE" d="M85.333 128c0 23.573-19.093 42.667-42.666 42.667S0 151.573 0 128s19.093-42.667 42.667-42.667h42.666z" /><path fill="#0ACF83" d="M85.333 213.333c0 23.574-19.093 42.667-42.666 42.667S0 236.907 0 213.333s19.093-42.666 42.667-42.666S85.333 189.76 85.333 213.333" /><path fill="#A259FF" d="M85.333 128c0-23.573 19.094-42.667 42.667-42.667s42.667 19.094 42.667 42.667S151.573 170.667 128 170.667S85.333 151.573 85.333 128" /></svg>
                </div>
                <div className="skill-name">Figma</div>
              </div>
              <div className="skill-item">
                <div className="skill-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128"><path fill="#639" d="M0 0h107.52C118.831 0 128 9.168 128 20.48v87.04c0 11.312-9.168 20.48-20.48 20.48H20.48C9.169 128 0 118.832 0 107.52Z" /><path fill="#fff" d="M45.836 117.762c-8.219-.008-13.293-4.64-13.195-13.16V83.05q0-6.481 3.793-9.852c4.543-4.379 15.082-4.644 19.53.067c2.743 2.417 3.778 7.355 3.532 11.964H50.06c.074-1.808-.024-4.554-1.09-5.586c-1.383-1.87-5.035-1.652-6.004.297c-.594 1.059-.89 2.621-.89 4.696v18.71q0 5.884 4.09 5.95q1.914-.001 2.905-1.39c.918-1.099 1.063-3.528.989-5.29h9.437c.645 8.969-4.652 15.254-13.66 15.144Zm29.957 0c-9.11.125-13.184-6.36-12.934-15.145h8.91c-.25 3.832 1.067 7.32 4.223 7.078q2.11 0 2.969-1.324c1.086-1.613 1.293-6.265-.266-8.066c-1.085-1.735-4.996-3.266-7.058-4.297q-4.423-2.115-6.367-5.027c-2.93-4.305-2.657-13.754 1.449-17.586c3.992-4.727 14.414-4.942 18.41-.098c2.465 2.496 3.54 7.41 3.332 11.934h-8.578c.074-1.86-.102-4.86-.824-5.95q-.762-1.39-2.871-1.39q-3.762.001-3.762 4.496c.027 3.18 1.27 4.488 4.16 5.816c3.742 1.453 8.5 3.938 10.227 6.942c5.144 9.16 1.613 23.148-11.02 22.613Zm28.77 0c-9.11.125-13.184-6.36-12.934-15.145h8.91c-.25 3.832 1.066 7.32 4.223 7.078q2.108 0 2.969-1.324c1.085-1.613 1.289-6.265-.266-8.066c-1.086-1.735-4.996-3.266-7.059-4.297q-4.423-2.115-6.367-5.027c-2.93-4.305-2.656-13.754 1.45-17.586c3.992-4.727 14.413-4.942 18.41-.098c2.464 2.496 3.539 7.41 3.332 11.934h-8.579c.07-1.86-.101-4.86-.824-5.95q-.762-1.39-2.87-1.39q-3.763.001-3.763 4.496c.028 3.18 1.27 4.488 4.16 5.816c3.743 1.453 8.5 3.938 10.227 6.942c5.145 9.16 1.614 23.148-11.02 22.613Z" /></svg>
                </div>
                <div className="skill-name">CSS Systems</div>
              </div>
            </div>
            { }
            { }
            <div className="skills-groups-wrapper">
              { }
              <div className="skills-group skills-core">
                <h3 className="skills-group-title">Core Expertise</h3>
                <div className="skills-tags">
                  <span className="skill-tag core">React</span>
                  <span className="skill-tag core">Next.js</span>
                  <span className="skill-tag core">TypeScript</span>
                  <span className="skill-tag core">Figma</span>
                  <span className="skill-tag core">Design Systems</span>
                  <span className="skill-tag core">Tailwind CSS</span>
                  <span className="skill-tag core">PostgreSQL</span>
                  <span className="skill-tag core">REST APIs</span>
                </div>
              </div>
              { }
              <div className="skills-group skills-support">
                <h3 className="skills-group-title">Supporting Tech</h3>
                <div className="skills-tags">
                  <span className="skill-tag support">LangChain</span>
                  <span className="skill-tag support">OpenAI API</span>
                  <span className="skill-tag support">Python</span>
                  <span className="skill-tag support">Supabase</span>
                  <span className="skill-tag support">Node.js</span>
                  <span className="skill-tag support">Hugging Face</span>
                  <span className="skill-tag support">Redis</span>
                </div>
              </div>
              { }
              <div className="skills-group skills-tools">
                <h3 className="skills-group-title">Tools & Workflow</h3>
                <div className="skills-tags">
                  <span className="skill-tag tool">Figma</span>
                  <span className="skill-tag tool">Framer</span>
                  <span className="skill-tag tool">Vercel</span>
                  <span className="skill-tag tool">Git/GitHub</span>
                  <span className="skill-tag tool">AWS</span>
                  <span className="skill-tag tool">Docker</span>
                  <span className="skill-tag tool">CI/CD</span>
                  <span className="skill-tag tool">Jest</span>
                  <span className="skill-tag tool">Cypress</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section section-5">
          <div className="section-5-content">
            <div className="section-5-text">
              <p className="section-5-paragraph">
                React, Next.js, TypeScript, and Tailwind CSS are my daily weapons. Paired with a product design background in Figma and Framer, I build conversion-optimized design systems that scale across teams and storefronts without breaking down; the kind that cut handoff time by 67% and let engineers ship independently without asking twice.
              </p>
              <p className="section-5-paragraph">
                On the AI side I build and evaluate production-grade intelligent systems using LangChain, OpenAI APIs, Python, and Hugging Face. I've engineered evaluation pipelines that doubled throughput, cut model failure rates by 30%, and optimized transformer architectures that reduced cloud operating costs by $15K annually. Not research for its own sake. Applied AI that ships and performs.
              </p>
              <p className="section-5-paragraph">
                I turn the most technically dense workflows into interfaces people actually understand and use. AI evaluation dashboards that cut analyst triage from hours to minutes. E-commerce platforms that moved $40K+ in incremental revenue in a single quarter. Every interface I ship is fast, purposeful, and built around the outcome it needs to drive.
              </p>
              <p className="section-5-paragraph">
                My workflow is clean architecture, tight iteration loops, and zero handoff friction. I take a product from Figma frame to production deploy in a day without compromising visual consistency, performance, or scalability. Full ownership. No gaps in the chain.
              </p>
            </div>
          </div>
        </section>
        { }
        <section className="section section-6">
          <div className="section-6-bg"></div>
          <div className="section-6-wrapper">
            <div className="section-6-content">
              <h2 className="section-6-title" data-effect26>
                <span className="section-6-word section-6-word-1">Welcome to</span>
                <span className="section-6-word section-6-word-2">My Works</span>
              </h2>
            </div>
          </div>
        </section>
        <section className="section section-7"></section>
        <section id="work" className="section section-8"></section>
        <section className="section section-9"></section>
        <section className="section section-10"></section>
        <section className="section section-11"></section>
        <section className="section section-12"></section>
        <section className="section section-13"></section>
        <section className="section section-14"></section>
        <section className="section section-15"></section>
        { }
      </div>
      <footer id="contact" data-lenis-prevent-touch>
        { }
        <div className="footer__bg-lines">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="footer__bg-line"
              style={{
                left: `${i * 5}%`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>
        { }
        <div className="footer__content">
          { }
          <div className="footer__titles">
            <h2 className="footer__title footer__title-1">Let's Create</h2>
            <div className="footer__cover-wrapper">
              <h2 className="footer__title footer__title--cover">Digital<br />Products</h2>
              { }
              <div className="footer__cover-beams">
                <div className="footer__cover-beam footer__cover-beam--1"></div>
                <div className="footer__cover-beam footer__cover-beam--2"></div>
                <div className="footer__cover-beam footer__cover-beam--3"></div>
              </div>
              { }
              <div className="footer__cover-sparkles"></div>
            </div>
          </div>
          { }
          <div className="footer__info">
            <div className="footer__info-block">
              <span className="footer__label">Email:</span>
              <a href="mailto:mirsadatbinrakib01@gmail.com" className="footer__link">mirsadatbinrakib01@gmail.com</a>
            </div>
            <div className="footer__info-block">
              <span className="footer__label">Work Model:</span>
              <span className="footer__text">Worldwide (Hybrid)</span>
            </div>
            <div className="footer__info-block">
              <span className="footer__label">Availability:</span>
              <span className="footer__text">Open to full-time, internship, freelance & contract</span>
            </div>
          </div>
          { }
          <div className="footer__socials">
            <a href="/MirSadatBinRakib_Resume.pdf" target="_blank" rel="noopener noreferrer" className="footer__social">Resume</a>
            <a href="https://github.com/Sadat-Rakib" target="_blank" rel="noopener noreferrer" className="footer__social">GitHub</a>
            <a href="https://www.linkedin.com/in/mir-sadat-bin-rakib/" target="_blank" rel="noopener noreferrer" className="footer__social">LinkedIn</a>
            <a href="https://x.com/msbr_dev" target="_blank" rel="noopener noreferrer" className="footer__social">Twitter/X</a>
            <a href="https://mirrin.vercel.app/" target="_blank" rel="noopener noreferrer" className="footer__social">Mirrin</a>
          </div>
          { }
          <div className="footer__copyright">
            <span>© 2026 MirSadat Dev. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  )
}